import { useState, useEffect } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import { pluginApi } from '@/lib/api';
import { useApiMutation } from '@/hooks/useApi';
import { Loader2, Save, UserCheck, RefreshCw } from 'lucide-react';
import { toast } from 'sonner';

export function StickyRolesPlugin({ serverId }: { serverId: string }) {
  const { data: plugins } = useQuery({
    queryKey: ['servers', serverId, 'plugins'],
    queryFn: () => pluginApi.getPlugins(serverId),
  });

  const stickyRolesPlugin = (plugins as any)?.find((p: any) => p.pluginId === 'sticky-roles');
  const isEnabled = stickyRolesPlugin?.enabled ?? false;

  const { mutate: togglePlugin } = useApiMutation(
    ({ enabled }: { enabled: boolean }) => 
      enabled ? pluginApi.enablePlugin(serverId, 'sticky-roles') : pluginApi.disablePlugin(serverId, 'sticky-roles'),
    { invalidateQueries: [['servers', serverId, 'plugins']] }
  );

  const { mutate: updateConfig, isPending: saving } = useApiMutation(
    (config: any) => pluginApi.updatePluginConfig(serverId, 'sticky-roles', config),
    { 
      onSuccess: () => toast.success('Configuration saved!'),
      invalidateQueries: [['servers', serverId, 'plugins']] 
    }
  );

  const [config, setConfig] = useState<any>({
    enabled: true,
    ignoreBots: true,
    ignoreManaged: true
  });

  useEffect(() => {
    if (stickyRolesPlugin?.config) {
      setConfig({ ...config, ...stickyRolesPlugin.config });
    }
  }, [stickyRolesPlugin]);

  const handleSave = () => {
    updateConfig(config);
  };

  if (!stickyRolesPlugin) return null;

  return (
    <div className="space-y-6 max-w-4xl mx-auto pb-12">
      <div className="glass-card p-6 flex items-center justify-between border-b border-white/10">
        <div className="flex items-center gap-4">
          <div className="p-3 bg-cyan-500/20 rounded-xl">
            <UserCheck className="w-6 h-6 text-cyan-400" />
          </div>
          <div>
            <h3 className="font-bold text-xl">Sticky Roles</h3>
            <p className="text-sm text-muted-foreground">
              Restore member roles when they rejoin.
            </p>
          </div>
        </div>
        <Switch
          checked={isEnabled}
          onCheckedChange={(enabled) => togglePlugin({ enabled })}
        />
      </div>

      {isEnabled && (
        <>
          <div className="glass-card p-6 space-y-3">
            <div className="flex items-center justify-between py-2 px-4 bg-white/5 rounded-lg">
              <div>
                <Label className="text-sm">Ignore Bot Roles</Label>
                <p className="text-xs text-muted-foreground">Don't save/restore roles managed by bots</p>
              </div>
              <Switch
                checked={config.ignoreBots}
                onCheckedChange={(checked) => setConfig({ ...config, ignoreBots: checked })}
              />
            </div>

            <div className="flex items-center justify-between py-2 px-4 bg-white/5 rounded-lg">
              <div>
                <Label className="text-sm">Ignore Managed Roles</Label>
                <p className="text-xs text-muted-foreground">Don't save integration-managed roles</p>
              </div>
              <Switch
                checked={config.ignoreManaged}
                onCheckedChange={(checked) => setConfig({ ...config, ignoreManaged: checked })}
              />
            </div>
          </div>

          <div className="glass-card p-6 bg-cyan-500/5 border-cyan-500/20">
            <div className="flex gap-3">
              <RefreshCw className="w-5 h-5 text-cyan-400 flex-shrink-0 mt-0.5" />
              <div className="text-sm">
                <p className="font-medium text-cyan-400 mb-1">How it works:</p>
                <ul className="list-disc list-inside space-y-1 text-muted-foreground">
                  <li>Automatically saves member roles when they leave</li>
                  <li>Restores saved roles when they rejoin</li>
                  <li>Helps maintain role consistency</li>
                  <li>Useful for preventing role-hopping exploits</li>
                </ul>
              </div>
            </div>
          </div>

          <div className="glass-card p-6 space-y-4">
            <h4 className="font-semibold">Available Commands</h4>
            <div className="p-3 bg-white/5 rounded-lg">
              <code className="text-cyan-400">/stickyroles [info]</code>
              <p className="text-muted-foreground mt-1">View sticky roles configuration</p>
            </div>
          </div>

          <div className="flex justify-end gap-3">
            <Button onClick={handleSave} disabled={saving} className="gap-2">
              {saving ? (
                <>
                  <Loader2 className="w-4 h-4 animate-spin" />
                  Saving...
                </>
              ) : (
                <>
                  <Save className="w-4 h-4" />
                  Save Configuration
                </>
              )}
            </Button>
          </div>
        </>
      )}
    </div>
  );
}
